/**
 * Cross-platform shortcut layer — per E-023 §D.
 *
 * `registerShortcuts({ open, close, ... })` returns an unregister function.
 * Each shortcut is a string list of bindings. The state-machine resolves
 * the `mod+k` open / `mod+k` prev clash:
 *
 *   - Bar closed             → mod+k OPENS
 *   - Bar open + input focus → mod+k is consumed by the input
 *   - Bar open + result focus → mod+k goes PREV (vi-style — uppercase K)
 *
 * Shortcut binding syntax (subset of Mousetrap):
 *
 *   "mod+k"      — Cmd/Ctrl + K
 *   "mod+enter"  — Cmd/Ctrl + Enter
 *   "/"          — slash key
 *   "escape"     — Escape
 *   "arrowdown"  — ArrowDown
 *   "shift+enter"
 *
 * All bindings are case-insensitive on the key part. The library does NOT
 * yet support sequence shortcuts (`g g`); that's queued for a future ADR.
 */
import { modKeyHeld } from "./platform.js";
function parseBinding(b) {
    const parts = b.toLowerCase().split("+");
    const out = { mod: false, shift: false, alt: false, key: "" };
    for (const p of parts) {
        if (p === "mod" || p === "cmd" || p === "ctrl")
            out.mod = true;
        else if (p === "shift")
            out.shift = true;
        else if (p === "alt" || p === "option")
            out.alt = true;
        else
            out.key = p;
    }
    return out;
}
function eventMatches(e, b) {
    if (b.mod !== modKeyHeld(e))
        return false;
    if (b.shift !== e.shiftKey)
        return false;
    if (b.alt !== e.altKey)
        return false;
    const key = e.key.toLowerCase();
    // Map common synonyms
    const norm = (k) => ({
        arrowdown: "arrowdown",
        arrowup: "arrowup",
        arrowleft: "arrowleft",
        arrowright: "arrowright",
        escape: "escape",
        enter: "enter",
        return: "enter",
        space: " ",
        slash: "/",
    })[k] ?? k;
    return norm(key) === norm(b.key);
}
const DEFAULTS = {
    open: ["mod+k", "/"],
    close: ["escape"],
    next: ["arrowdown"],
    prev: ["arrowup"],
    select: ["enter"],
    newTab: ["mod+enter", "shift+enter"],
    copy: ["mod+c"],
};
/**
 * Register global shortcuts. Returns an unregister function.
 *
 * The handlers are called when the corresponding shortcut fires. The
 * `isOpen` and `isInputFocused` predicates resolve the mod+k state machine.
 */
export function registerShortcuts(config, handlers) {
    if (typeof window === "undefined") {
        // SSR — no-op.
        return () => { };
    }
    const cfg = { ...DEFAULTS, ...config };
    const parsed = {
        open: cfg.open.map(parseBinding),
        close: cfg.close.map(parseBinding),
        next: cfg.next.map(parseBinding),
        prev: cfg.prev.map(parseBinding),
        select: cfg.select.map(parseBinding),
        newTab: cfg.newTab.map(parseBinding),
        copy: cfg.copy.map(parseBinding),
    };
    function onKeyDown(e) {
        const open = handlers.isOpen();
        const inputFocused = handlers.isInputFocused();
        // Close — only when the bar is open.
        if (open && parsed.close.some((b) => eventMatches(e, b))) {
            e.preventDefault();
            handlers.onClose?.();
            return;
        }
        // Open — only when the bar is closed.
        if (!open && parsed.open.some((b) => eventMatches(e, b))) {
            // Don't grab "/" if the user is typing in another input.
            const target = e.target;
            const isTypingElsewhere = !!target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable);
            if (e.key === "/" && isTypingElsewhere)
                return;
            e.preventDefault();
            handlers.onOpen?.();
            return;
        }
        // The remaining shortcuts only fire when the bar is open.
        if (!open)
            return;
        // newTab BEFORE select so mod+enter beats plain enter.
        if (parsed.newTab.some((b) => eventMatches(e, b))) {
            e.preventDefault();
            handlers.onNewTab?.();
            return;
        }
        if (parsed.select.some((b) => eventMatches(e, b))) {
            e.preventDefault();
            handlers.onSelect?.();
            return;
        }
        if (parsed.next.some((b) => eventMatches(e, b))) {
            e.preventDefault();
            handlers.onNext?.();
            return;
        }
        if (parsed.prev.some((b) => eventMatches(e, b))) {
            // mod+k clash: when input is focused, the input consumes mod+k itself
            // (browsers don't fire any application shortcut). When result is focused,
            // mod+k goes prev. The 'prev' binding is `arrowup` by default — no clash.
            // If the user binds prev to mod+k, the inputFocused gate prevents the
            // ambiguity.
            if (e.key.toLowerCase() === "k" && modKeyHeld(e) && inputFocused)
                return;
            e.preventDefault();
            handlers.onPrev?.();
            return;
        }
        if (parsed.copy.some((b) => eventMatches(e, b))) {
            // Only intercept mod+c when a result is focused; otherwise the user is
            // likely copying the input text.
            if (inputFocused)
                return;
            e.preventDefault();
            handlers.onCopy?.();
            return;
        }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
}
export { detectPlatform, modKeyHeld, modLabel } from "./platform.js";
//# sourceMappingURL=index.js.map